/**
 * @typedef {object} ShopifyReplaceConfig
 * @property {object} policy
 * @property {string[]} policy.text
 * @property {number} policy.limit
 * @property {string[]} reasonNote
 * @property {string[]} messageToCustomer
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Loading from '../../../LoadingComponent/LoadingComponent';
import ImageIcon from '../../../ImageIcon/ImageIcon';
import { oneBtnPopupCreator, twoBtnPopupCreator } from '../../../popups/ConfirmPopup/helper';
import ReplacementOrderItem from '../ReplacementOrderItem/ReplacementOrderItem';
import { SHOPIFY_FINANCIAL_STATUSES } from '../../../../constants/shopifyCancelAndRefunds';
import { runSafely, messageToClientFormat } from '../../../../util';
import { DraftHelpers } from '../../../../util/Draft';
import { EMAIL, SMS } from '../../../../constants/conversationTypes';
import { PolicyButton } from '../../../common/buttonsComponents';

import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import selectors from '../../../../selectors';
import { setSelectedTask } from '../../../../actions/tools/OrdersSearchActions';
import { shopifyReplaceOrder, shopifyAddOrderNote } from '../../../../actions/tools/ShopifyActions';
import {
  extractSelectedTicketFromState,
  isSelectedTicketTaken,
  getNewMessagesEditor,
} from '../../../../extractFromState';
import { setShowTools, saveNewMessageEditorState, addNoteToSelectedTicket } from '../../../../actions';
import {
  OrderTaskWrapper,
  OrderTaskTitle,
  OrderTaskDescription,
  BackButton,
  FlexContainer,
  FlexColumn,
  CommentTextarea,
  OrderListWrapper,
  OrderListTitle,
  OrderList,
  OrderTaskControls,
  OrderBackButton,
  OrderActionButton,
} from './ShopifyReplaceOrder.styles';

class ShopifyReplaceOrder extends Component {
  static propTypes = {
    user: PropTypes.object,
    isSelectedTicketTaken: PropTypes.bool,
    selectedShop: PropTypes.string.isRequired,
    selectedOrder: PropTypes.object.isRequired,
    selectedConfig: PropTypes.shape({
      replaceSupport: PropTypes.bool.isRequired,
      replaceConfig: PropTypes.shape({
        policy: PropTypes.shape({
          text: PropTypes.arrayOf([PropTypes.string]),
          limit: PropTypes.number,
        }).isRequired,
        reasonNote: PropTypes.arrayOf(PropTypes.string),
        messageToCustomer: PropTypes.arrayOf(PropTypes.string),
      }).isRequired,
    }).isRequired,
    selectedTicket: PropTypes.object.isRequired,
    addNoteToSelectedTicket: PropTypes.func,
    setSelectedTask: PropTypes.func,
    shopifyReplaceOrder: PropTypes.func,
    shopifyAddOrderNote: PropTypes.func,
    setShowTools: PropTypes.func,
    saveNewMessageEditorState: PropTypes.func,
  };

  static defaultProps = {
    user: {},
    isSelectedTicketTaken: false,
    setShowTools: () => {},
    saveNewMessageEditorState: () => {},
    addNoteToSelectedTicket: () => Promise.resolve(),
    shopifyReplaceOrder: () => Promise.resolve(),
    shopifyAddOrderNote: () => Promise.resolve(),
  };

  /**
   * @type {ShopifyReplaceConfig}
   */
  defaultReplaceConfig = {
    policy: {
      text: ['Replacements are allowed for this partner'],
      limit: 1,
    },
    reasonNote: ['A replacement was issued'],
    messageToCustomer: ["We're sorry for the inconvenience. Your replacement has been processed"],
  };

  state = {
    replacementOrder: {},
    replaceOrderItems: {},
    isPolicyAccepted: false,
    isRequesting: false,
    reasonNote: this.getReasonNote(),
  };

  /**
   * @type {ShopifyReplaceConfig}
   */
  getReplaceConfig() {
    return {
      ...this.defaultReplaceConfig,
      ...this.props.selectedConfig.replaceConfig,
    };
  }

  isOrderRefunded() {
    return this.props.selectedOrder.financial_status === SHOPIFY_FINANCIAL_STATUSES.REFUNDED;
  }

  isReplaceAllow() {
    return this.props.selectedConfig.replaceSupport && !this.isOrderRefunded();
  }

  goBack = () => this.props.setSelectedTask(null);

  acceptPolicy = () => this.setState({ isPolicyAccepted: true });

  handlerTextInputChange = ({ target: { name, value } }) => this.setState({ [name]: value });

  getPolicyText() {
    return this.getReplaceConfig().policy.text.map((chunk) => (
      <p key={`shopify-replace-order-policy-${chunk}`}>{chunk}</p>
    ));
  }

  getPolicyLimit() {
    return this.getReplaceConfig().policy.limit;
  }

  getReasonNote() {
    return this.getReplaceConfig()
      .reasonNote.reduce((string, chunk) => `${string}${chunk}\n`, '')
      .trim();
  }

  getMessageToCustomerText() {
    return this.getReplaceConfig()
      .messageToCustomer.reduce((string, chunk) => `${string}${chunk}\n`, '')
      .trim();
  }

  get orderItems() {
    return this.props.selectedOrder.line_items;
  }

  get replacedOrderItems() {
    return this.orderItems
      .map((orderItem) => ({
        variant_id: orderItem.variant_id,
        quantity: this.state.replaceOrderItems[orderItem.id],
      }))
      .filter(({ quantity }) => quantity > 0);
  }

  get replaceOrderPayload() {
    return {
      shopName: this.props.selectedShop,
      simplrAccountId: this.props.selectedTicket.simplrAccountPtrId,
      expertId: this.props.user.id,
      items: this.replacedOrderItems,
    };
  }

  get addOrderNotePayload() {
    const channelsWithNoPlatformTicketId = [EMAIL, SMS];
    const ticketNumber =
      channelsWithNoPlatformTicketId.indexOf(this.props.selectedTicket.ticketType) > -1
        ? this.props.selectedTicket.simplrTicketNumber
        : this.props.selectedTicket.platformTicketId;

    return {
      shopName: this.props.selectedShop,
      simplrAccountId: this.props.selectedTicket.simplrAccountPtrId,
      note: `
      REPLACEMENT ORDER:
      ORIGINAL - "${this.props.selectedOrder.name}"
      REPLACEMENT - "${this.state.replacementOrder.name}"
      ${this.props.selectedTicket.ticketType} TICKET - "${ticketNumber}"
      REASON:
      ${this.state.reasonNote}`,
    };
  }

  handleReplaceBtnClick = () => {
    if (this.isPayloadInvalid()) {
      return;
    }
    twoBtnPopupCreator({
      content: {
        main: 'Are you sure you want to replace the order?',
        iconPath: ImageIcon.type.alertGreenBell,
      },
      btnConfig: {
        dismiss: 'DISMISS',
        confirm: 'REPLACE ORDER',
      },
      onConfirm: this.replaceHandler,
    });
  };

  isPayloadInvalid() {
    if (this.replacedOrderItems.length < 1) {
      oneBtnPopupCreator({
        content: {
          main: 'Please add at least one item to create replacement',
          iconPath: ImageIcon.type.errorEscalation,
        },
        btnConfig: 'CLOSE',
      });

      return true;
    }

    return false;
  }

  replaceHandler = async () => {
    try {
      this.setState({ isRequesting: true });
      const replacementOrder = await this.replaceOrderRequest();
      this.setState(() => ({ replacementOrder }));
      await runSafely(this.addNotesRequest);
      this.handleSuccess();
    } catch (error) {
      this.handleFailed(error);
    } finally {
      this.setState({ isRequesting: false });
    }
  };

  replaceOrderRequest = () => this.props.shopifyReplaceOrder(this.props.selectedOrder.id, this.replaceOrderPayload);

  addNotesRequest = () =>
    Promise.all([this.addNoteToParentOrder(), this.addNoteToReplacementOrder(), this.addNoteToConversation()]);

  addNoteToParentOrder = () => this.props.shopifyAddOrderNote(this.props.selectedOrder.id, this.addOrderNotePayload);

  addNoteToReplacementOrder = () =>
    this.props.shopifyAddOrderNote(this.state.replacementOrder.id, this.addOrderNotePayload);

  addNoteToConversation = async () => this.props.addNoteToSelectedTicket(this.addOrderNotePayload.note);

  handleSuccess = () => {
    if (this.props.isSelectedTicketTaken) {
      const { ticketType, communicationType } = this.props.selectedTicket;
      const macro = messageToClientFormat(this.getMessageToCustomerText(), ticketType, communicationType);
      const editorState = DraftHelpers.setContents(macro, this.props.editorState);
      this.props.saveNewMessageEditorState(this.props.selectedTicket.ticketId, editorState);
    }
    this.props.setSelectedTask(null);
    this.props.setShowTools(false);
    oneBtnPopupCreator({
      content: {
        main: `Successful Replacement ${this.props.selectedOrder.name} -> ${this.state.replacementOrder.name}`,
        iconPath: ImageIcon.type.alertGreenBell,
      },
      btnConfig: this.props.isSelectedTicketTaken ? 'REPLY TO CUSTOMER' : 'CLOSE',
    });
  };

  handleFailed = (error = {}) => {
    const { response = {} } = error;
    const { body = {} } = response;
    oneBtnPopupCreator({
      content: {
        main: body.Message || 'Replace Order failed',
        iconPath: ImageIcon.type.errorEscalation,
      },
      btnConfig: 'CLOSE',
    });
  };

  checkOrderMeetsPolicy() {
    return (
      this.props.selectedOrder.financial_status === SHOPIFY_FINANCIAL_STATUSES.PARTIALLY_REFUNDED ||
      this.props.selectedOrder.financial_status === SHOPIFY_FINANCIAL_STATUSES.PAID ||
      this.props.selectedOrder.financial_status === SHOPIFY_FINANCIAL_STATUSES.PARTIALLY_PAID
    );
  }

  onQuantityChanged = (index) => (quantity) => {
    const { id } = this.orderItems[index];
    this.setState(({ replaceOrderItems }) => ({
      replaceOrderItems: {
        ...replaceOrderItems,
        [id]: quantity,
      },
    }));
  };

  notAllowPlaceholder() {
    const content = {
      title: 'Replacement not allowed',
      reason: '',
    };
    if (!this.props.selectedConfig.replaceSupport) {
      content.title = 'Replacement is not supported by this partner';
      content.reason =
        'This partner does not currently support replacement orders via this platform.' +
        ' Please select another action or escalate the ticket.';
    } else if (this.isOrderRefunded) {
      content.title = 'Order has already been refunded';
      content.reason =
        'You cannot replace this order because the order has already been refunded.' +
        ' Please select another action instead.';
    }

    return (
      <div className="action-unavailable-page text-center">
        <div className="flex-content">
          <div className="row">
            <div className="col-xs-12">
              <ImageIcon src={ImageIcon.type.xGreen} className="svg top-image" />
            </div>
          </div>
          <div className="row">
            <div className="col-xs-12">
              <p className="title-label">{content.title}</p>
            </div>
          </div>
          <div className="row">
            <div className="col-xs-12">
              <p className="instructions-label">{content.reason}</p>
            </div>
          </div>
          <div className="row">
            <div className="col-xs-12 text-center">
              <BackButton type="button" onClick={this.goBack}>
                BACK TO ACTIONS
              </BackButton>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderPolicy() {
    const isMeetsPolicy = this.checkOrderMeetsPolicy();
    const imgSrc = isMeetsPolicy ? ImageIcon.type.checkmarkGreen : ImageIcon.type.xGreen;
    const alert = isMeetsPolicy ? 'This order meets the condition!' : 'This order does not meet the condition!';
    const btnAction = isMeetsPolicy ? this.acceptPolicy : this.goBack;

    return (
      <div className="policy-page text-center">
        <div className="flex-content">
          <ImageIcon src={ImageIcon.type.magnifiedDocument} className="top-image" />
          <p className="policy-title">What is the Replacement policy?</p>
          {this.getPolicyText()}
          <p>Limit {this.getPolicyLimit()} replacements per day</p>
          <hr className="policy-divider" />
          <ImageIcon src={imgSrc} />
          <p className="policy-alert">{alert}</p>
          <div className="row">
            <div className="col-xs-12 text-center">
              <PolicyButton type="button" onClick={btnAction}>
                {isMeetsPolicy ? 'GO' : 'BACK TO ACTIONS'}
              </PolicyButton>
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    if (!this.isReplaceAllow()) {
      return this.notAllowPlaceholder();
    }
    if (!this.state.isPolicyAccepted) {
      return this.renderPolicy();
    }
    if (this.state.isRequesting) {
      return <Loading label="Send A Replacement..." />;
    }

    return (
      <OrderTaskWrapper>
        <OrderTaskTitle>Send A Replacement</OrderTaskTitle>
        <OrderTaskDescription>
          {this.getPolicyText()}
          <p>Limit {this.getPolicyLimit()} replacements per day</p>
        </OrderTaskDescription>
        <OrderListWrapper className="clearfix">
          <OrderListTitle>Replacement Order</OrderListTitle>
          <OrderList>
            {this.orderItems.map((item, index) => (
              <ReplacementOrderItem
                key={item.id}
                order={this.props.selectedOrder}
                orderItem={item}
                quantity={this.state.replaceOrderItems[item.id]}
                onQuantityChanged={this.onQuantityChanged(index)}
              />
            ))}
          </OrderList>
        </OrderListWrapper>
        <FlexContainer>
          <FlexColumn>
            <h4>Replacement Comment</h4>
            <CommentTextarea name="reasonNote" value={this.state.reasonNote} onChange={this.handlerTextInputChange} />
          </FlexColumn>
        </FlexContainer>
        <OrderTaskControls>
          <OrderBackButton onClick={this.goBack}>BACK</OrderBackButton>
          <OrderActionButton onClick={this.handleReplaceBtnClick}>REPLACE ORDER</OrderActionButton>
        </OrderTaskControls>
      </OrderTaskWrapper>
    );
  }
}

const mapStateToProps = (state) => ({
  selectedOrder: selectors.tools.getSelectedOrder(state),
  selectedConfig: selectors.tools.getSelectedShopifyConfig(state),
  selectedShop: selectors.tools.getSelectedShop(state),
  selectedTicket: extractSelectedTicketFromState(state),
  isSelectedTicketTaken: isSelectedTicketTaken(state),
  user: state.user,
  editorState: getNewMessagesEditor(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      setSelectedTask,
      addNoteToSelectedTicket,
      setShowTools,
      saveNewMessageEditorState,
      shopifyAddOrderNote,
      shopifyReplaceOrder,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(ShopifyReplaceOrder);
